import { kindEntry, dateModeOf, DATE_MODES } from './specSchema';

// One plain-English line per sub-object, for the collapsed question-panel headers.
//
// The rule these follow: where the mechanic is counterintuitive, state it LITERALLY rather than
// paraphrasing. A confidently-wrong summary sitting above a Lock button is worse than no summary,
// and three of this schema's mechanics read backwards in English —
//   * `latest_n` under related_filter is PER RELATED COMPANY, not a total;
//   * an OMITTED `latest_n` means every matching document, not one;
//   * `include_self: false` means the scored company's own filings are absent from its own score.

const EM_DASH = '—';

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const list = (items) => {
  const arr = (items || []).filter(Boolean);
  if (arr.length === 0) return EM_DASH;
  if (arr.length === 1) return arr[0];
  return `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`;
};

export const summarizeTrigger = (definition) => {
  const trigger = isObject(definition && definition.trigger) ? definition.trigger : {};
  if (!trigger.kind) return 'No trigger set — this spec has nothing to make it run.';

  if (trigger.kind === 'document_available') {
    const types = Array.isArray(trigger.doc_types) ? trigger.doc_types : [];
    if (types.length === 0) return 'Fires when a document is ingested, but no document types are listed yet.';
    return `Fires when a new ${list(types)} is ingested.`;
  }

  if (trigger.kind === 'pre_earnings_release') {
    const lead = trigger.lead_time_days;
    const when = Number.isFinite(Number(lead))
      ? `${lead} day${Number(lead) === 1 ? '' : 's'}`
      : 'an unset number of days';
    return `Fires ${when} before a company’s earnings release.`;
  }

  const entry = kindEntry('trigger', trigger.kind);
  if (entry) return `${trigger.kind} — ${entry.caveat || entry.blurb}`;
  return `${trigger.kind} — this editor does not recognise that trigger kind.`;
};

const summarizeDocTypeRules = (rules, perRelated) => {
  const arr = Array.isArray(rules) ? rules : [];
  if (arr.length === 0) return 'no document types listed yet';
  return arr
    .map((rule) => {
      const type = (isObject(rule) && rule.doc_type) || 'an unnamed type';
      if (!isObject(rule) || rule.latest_n === undefined || rule.latest_n === null) {
        return `EVERY ${type}${perRelated ? ' per related company' : ''} in the window`;
      }
      return `the latest ${rule.latest_n} ${type}${perRelated ? ' PER RELATED COMPANY' : ''}`;
    })
    .join(', ');
};

const summarizeWindow = (range) => {
  if (!isObject(range)) return '';
  const mode = dateModeOf(range);
  const base = `${range.from || '?'} → ${range.to || '?'}`;
  if (mode === DATE_MODES.ANCHORED) return `${base} anchored on ${range.anchor || 'nothing yet'}`;
  return base;
};

export const summarizeDocumentSet = (definition) => {
  const set = isObject(definition && definition.document_set) ? definition.document_set : {};
  const documents = isObject(set.documents) ? set.documents : {};
  const kind = documents.kind;

  if (!kind) return 'No document set — nothing decides which documents the model reads.';

  const fanout = isObject(set.fanout) && set.fanout.kind ? set.fanout.kind : EM_DASH;
  const tail = ` Fanout: ${fanout}.`;

  if (kind === 'triggering_document') {
    return `Scores the triggering company from the single document that fired the trigger.${tail}`;
  }

  if (kind === 'filter') {
    return `Scores the triggering company from its OWN ${summarizeDocTypeRules(
      documents.doc_types,
      false
    )}, published on or before the occasion date.${tail}`;
  }

  if (kind === 'related_filter') {
    const scope = isObject(documents.companies) ? documents.companies : {};
    const kinds = Array.isArray(scope.relationship_kinds) ? scope.relationship_kinds : [];
    const self = scope.include_self === true;
    return [
      `Scores the triggering company from OTHER companies’ documents — its ${list(kinds)}${
        kinds.length ? '' : ' (no relationship kinds picked)'
      }.`,
      `Reads ${summarizeDocTypeRules(documents.doc_types, true)}, ${summarizeWindow(documents.date_range)}.`,
      self
        ? 'include_self is on, so the scored company’s own filings are read alongside its related companies’.'
        : 'include_self is off, so the scored company’s OWN filings are NOT read.',
      // Stated here as well as on the kind card because this summary is what the feed-view prompt
      // panel shows an analyst reading the scores, who never sees the authoring UI.
      'This kind is experimental: its related-company data is not lookahead-safe, so historical rows may read relationships that did not exist yet.',
      `Fanout: ${fanout}.`,
    ].join(' ');
  }

  if (kind === 'screen') {
    const types = Array.isArray(documents.doc_types) ? documents.doc_types : [];
    const restrict = isObject(documents.restrict_to_companies);
    return [
      `Broad semantic search over ${list(types)}, ${summarizeWindow(documents.date_range)}.`,
      restrict
        ? 'restrict_to_companies is set, but it does NOT bound screen retrieval — the search stays corpus-wide and the model identifies peers from context.'
        : 'Retrieval is corpus-wide.',
      `Fanout: ${fanout}.`,
    ].join(' ');
  }

  return `${kind} — this editor does not recognise that documents kind.${tail}`;
};

export const summarizeScoreGen = (definition) => {
  const gen = isObject(definition && definition.score_gen) ? definition.score_gen : {};
  if (!gen.model && !gen.prompt) return 'No model or prompt set yet.';

  const parts = [gen.model || 'no model set'];
  if (gen.reasoning_effort) parts.push(`reasoning effort ${gen.reasoning_effort}`);
  if (gen.max_output_tokens !== undefined && gen.max_output_tokens !== null) {
    parts.push(`${Number(gen.max_output_tokens).toLocaleString()} output tokens`);
  }
  return parts.join(' · ');
};

// The feed names a spec publishes. Single-score keeps the bare name; multi-score suffixes every
// feed, INCLUDING when a score is published alone — naming keys on the declared count.
//
// A score may override its derived name with `feed_name`, which replaces the WHOLE name rather than
// the suffix: `score_spec_<feed_name>`, no spec id in it. That is the point of the field — a v4
// re-cut keeps publishing to v3's feed — and it is also why the override carries no version, so the
// list here is the only place an operator sees that a feed name has been detached from the spec id.
export const feedNamesFor = (definition) => {
  const d = definition || {};
  const id = d.score_spec_id;
  if (!id) return [];
  const output = isObject(d.output_extraction) ? d.output_extraction : {};
  const scores = Array.isArray(output.scores) ? output.scores : null;

  if (!scores) return [`score_spec_${id}`];

  const derived = (score) =>
    scores.length === 1 ? `score_spec_${id}` : `score_spec_${id}__${(isObject(score) && score.name) || 'unnamed'}`;

  return scores.map((score) => {
    const override = isObject(score) && typeof score.feed_name === 'string' ? score.feed_name.trim() : '';
    return override ? `score_spec_${override}` : derived(score);
  });
};

export const summarizeOutput = (definition) => {
  const output = isObject(definition && definition.output_extraction) ? definition.output_extraction : {};
  const scores = Array.isArray(output.scores) ? output.scores : null;
  // Two scopes, counted apart: a broadcast column is on every feed this spec publishes, a per-score
  // one is on exactly one of them. A single total would read as the former and be wrong.
  const broadcastExtras = isObject(output.extra_columns) ? Object.keys(output.extra_columns).length : 0;
  const scopedExtras = (scores || []).reduce(
    (total, score) =>
      total + (isObject(score) && isObject(score.extra_columns) ? Object.keys(score.extra_columns).length : 0),
    0
  );
  const shape = output.output_schema
    ? 'output_schema declared'
    : 'instruction-only JSON — the shape is requested in prose, not guaranteed';

  let head;
  if (scores) {
    head =
      scores.length === 0
        ? 'No scores declared'
        : `${scores.length} score${scores.length === 1 ? '' : 's'}: ${list(
            scores.map((s) => (isObject(s) && s.name) || '?')
          )}`;
  } else if (output.score_jsonpath) {
    head = `One score from ${output.score_jsonpath}`;
  } else {
    head = 'No score declared — this spec extracts nothing';
  }

  const feeds = feedNamesFor(definition);
  const feedLine = feeds.length ? ` Publishes ${list(feeds)}.` : '';

  const extraLine = [
    broadcastExtras ? `${broadcastExtras} broadcast extra column${broadcastExtras === 1 ? '' : 's'}` : null,
    scopedExtras ? `${scopedExtras} per-score` : null,
  ]
    .filter(Boolean)
    .join(', ');

  return `${head}. ${shape}.${extraLine ? ` ${extraLine}.` : ''}${feedLine}`;
};

export const SUMMARIZERS = {
  trigger: summarizeTrigger,
  document_set: summarizeDocumentSet,
  score_gen: summarizeScoreGen,
  output_extraction: summarizeOutput,
};
